import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ParticleField from "./ParticleField";

const vib = (p) => { try { navigator.vibrate?.(p); } catch (_) {} };

const STORY_LINES = [
  "Defense protocol activated.",
  "Scanning external input...",
  "Corruption nodes detected.",
  "Stability decreasing.",
  "User adapting...",
  "Pattern recognition improving.",
  "Why are you resisting?",
  "System recalibrating...",
  "You were meant to find this.",
  "You are not defending the system.",
  "You ARE the system."
];

const TILE = 36;

const GridBackground = () => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);
    let frame = 0; let raf;
    const draw = () => {
      raf = requestAnimationFrame(draw); frame++;
      const W = canvas.width; const H = canvas.height;
      ctx.fillStyle = "#000"; ctx.fillRect(0, 0, W, H);
      ctx.strokeStyle = "rgba(0,245,255,0.045)"; ctx.lineWidth = 0.5;
      const cols = Math.ceil(W / TILE); const rows = Math.ceil(H / TILE);
      for (let i = 0; i <= cols; i++) { ctx.beginPath(); ctx.moveTo(i * TILE, 0); ctx.lineTo(i * TILE, H); ctx.stroke(); }
      for (let j = 0; j <= rows; j++) { ctx.beginPath(); ctx.moveTo(0, j * TILE); ctx.lineTo(W, j * TILE); ctx.stroke(); }
      if (frame % 8 === 0) {
        const rx = Math.floor(Math.random() * cols) * TILE;
        const ry = Math.floor(Math.random() * rows) * TILE;
        ctx.fillStyle = `rgba(0,255,136,${Math.random() * 0.06})`;
        ctx.fillRect(rx + 1, ry + 1, TILE - 2, TILE - 2);
      }
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, display: "block" }} />;
};

// ── Virtual Joystick ─────────────────────────────────────────────────────────
const VirtualJoystick = ({ onMove, color = "#00f5ff", label }) => {
  const baseRef = useRef(null);
  const stickRef = useRef(null);
  const activeTouchRef = useRef(null);
  const RADIUS = 48;
  const DEADZONE = 0.12;

  const resolve = (touch) => {
    const rect = baseRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    let dx = touch.clientX - cx;
    let dy = touch.clientY - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > RADIUS) { dx = (dx / dist) * RADIUS; dy = (dy / dist) * RADIUS; }
    return { dx, dy, nx: dx / RADIUS, ny: dy / RADIUS };
  };

  const emit = (nx, ny) => {
    const mag = Math.sqrt(nx * nx + ny * ny);
    if (mag < DEADZONE) { onMove({ x: 0, y: 0 }); return; }
    const scale = (mag - DEADZONE) / (1 - DEADZONE);
    onMove({ x: (nx / mag) * scale, y: (ny / mag) * scale });
  };

  const onTS = (e) => {
    e.preventDefault(); e.stopPropagation();
    const t = e.changedTouches[0];
    activeTouchRef.current = t.identifier;
    const { dx, dy, nx, ny } = resolve(t);
    stickRef.current.style.transform = `translate(${dx}px,${dy}px)`;
    emit(nx, ny);
  };
  const onTM = (e) => {
    e.preventDefault(); e.stopPropagation();
    for (const t of e.changedTouches) {
      if (t.identifier !== activeTouchRef.current) continue;
      const { dx, dy, nx, ny } = resolve(t);
      stickRef.current.style.transform = `translate(${dx}px,${dy}px)`;
      emit(nx, ny);
    }
  };
  const onTE = (e) => {
    e.preventDefault(); e.stopPropagation();
    for (const t of e.changedTouches) {
      if (t.identifier !== activeTouchRef.current) continue;
      activeTouchRef.current = null;
      stickRef.current.style.transform = "translate(0px,0px)";
      onMove({ x: 0, y: 0 });
    }
  };

  const rgb = color === "#00f5ff" ? "0,245,255" : "0,255,136";
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      {label && <div style={{ fontSize: 8, letterSpacing: 3, color, opacity: 0.5, fontFamily: "monospace" }}>{label}</div>}
      <div ref={baseRef} onTouchStart={onTS} onTouchMove={onTM} onTouchEnd={onTE}
        style={{ width: RADIUS * 2, height: RADIUS * 2, borderRadius: "50%", background: `rgba(${rgb},0.06)`, border: `1.5px solid ${color}44`, position: "relative", touchAction: "none", userSelect: "none", boxShadow: `0 0 20px ${color}18` }}>
        <div ref={stickRef}
          style={{ width: 36, height: 36, borderRadius: "50%", background: `${color}55`, border: `1.5px solid ${color}`, position: "absolute", top: "50%", left: "50%", marginTop: -18, marginLeft: -18, boxShadow: `0 0 10px ${color}80`, pointerEvents: "none", willChange: "transform" }} />
      </div>
    </div>
  );
};

const SecretGame = ({ onExit }) => {
  const canvasRef      = useRef(null);
  const animationRef   = useRef(null);
  const audioCtxRef    = useRef(null);
  const moveJoyRef     = useRef({ x: 0, y: 0 });
  const aimJoyRef      = useRef({ x: 0, y: 0 });
  const keys           = useRef({});
  const playerRef      = useRef({});
  const enemiesRef     = useRef([]);
  const bulletsRef     = useRef([]);
  const scoreRef       = useRef(0);
  const levelRef       = useRef(1);
  const spawnTimerRef  = useRef(0);
  const shakeRef       = useRef(0);
  const glitchRef      = useRef(0);
  const pulseRef       = useRef(0);
  const bossHueRef     = useRef(0);
  const slowMoRef      = useRef(0);
  const shockwaveRef   = useRef(null);
  const frameCountRef  = useRef(0);
  const isShootingRef  = useRef(false);
  const shootCdRef     = useRef(0);
  const aimDirRef      = useRef({ x: 0, y: -1 });
  const gameOverRef    = useRef(false);
  const bestScoreRef   = useRef(parseInt(localStorage.getItem("neural-override-best") || "0"));

  const [story,        setStory]        = useState(STORY_LINES[0]);
  const [gameOver,     setGameOver]     = useState(false);
  // "start" | "playing" | "resetting" — cycling "resetting"→"playing" forces useEffect re-run
  const [gameState,    setGameState]    = useState("start");
  const [displayScore, setDisplayScore] = useState(0);
  const [displayBest,  setDisplayBest]  = useState(bestScoreRef.current);
  const [displayLevel, setDisplayLevel] = useState(1);
  const [isMobile,     setIsMobile]     = useState(false);

  useEffect(() => {
    setIsMobile("ontouchstart" in window || navigator.maxTouchPoints > 0);
  }, []);

  const handleMoveJoy = useCallback((v) => { moveJoyRef.current = v; }, []);
  const handleAimJoy  = useCallback((v) => { aimJoyRef.current  = v; }, []);

  const playBeep = useCallback((freq, duration, type = "square") => {
    if (!audioCtxRef.current) return;
    try {
      const ac = audioCtxRef.current;
      const osc = ac.createOscillator(); const gain = ac.createGain();
      osc.type = type; osc.frequency.value = freq;
      osc.connect(gain); gain.connect(ac.destination);
      gain.gain.setValueAtTime(0.12, ac.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + duration);
      osc.start(); osc.stop(ac.currentTime + duration);
    } catch (_) {}
  }, []);

  // Hard-reset: cancel loop, clear all refs, then cycle gameState so useEffect re-fires
  const doReset = useCallback(() => {
    cancelAnimationFrame(animationRef.current);
    enemiesRef.current  = []; bulletsRef.current = [];
    scoreRef.current    = 0;  levelRef.current   = 1;
    spawnTimerRef.current = 0; frameCountRef.current = 0;
    gameOverRef.current = false; shakeRef.current = 0; glitchRef.current = 0;
    pulseRef.current    = 0;  bossHueRef.current  = 0;
    slowMoRef.current   = 0;  shockwaveRef.current = null;
    isShootingRef.current = false; shootCdRef.current = 0;
    aimDirRef.current   = { x: 0, y: -1 };
    moveJoyRef.current  = { x: 0, y: 0 };
    aimJoyRef.current   = { x: 0, y: 0 };
    keys.current        = {};
    setDisplayScore(0); setDisplayLevel(1);
    setStory(STORY_LINES[0]); setGameOver(false);
    setGameState("resetting");
  }, []);

  // Bounce "resetting" → "playing"
  useEffect(() => {
    if (gameState === "resetting") setGameState("playing");
  }, [gameState]);

  useEffect(() => {
    if (gameState !== "playing") return;

    const canvas = canvasRef.current;
    const ctx    = canvas.getContext("2d");

    if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
      try { audioCtxRef.current.close(); } catch (_) {}
    }
    audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();

    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);

    playerRef.current = { x: canvas.width / 2, y: canvas.height / 2, size: 14, speed: 4.5 };

    const spawnEnemy = () => {
      const lv = levelRef.current;
      const isBoss = lv >= 8 && Math.random() < 0.15;
      enemiesRef.current.push({
        x: Math.random() * canvas.width, y: -20,
        size:  isBoss ? 40 : 14 + lv * 0.5,
        speed: isBoss ? 0.8 : 1 + lv * 0.2,
        hp:    isBoss ? 15 : 1 + Math.floor(lv / 4),
        boss:  isBoss,
      });
    };

    const shoot = () => {
      const player = playerRef.current;
      const aim    = aimDirRef.current;
      const lv     = levelRef.current;
      if (lv >= 10) { playBeep(1200, 0.02, "sawtooth"); return; }
      const base = Math.atan2(aim.y, aim.x);
      const cnt  = lv >= 4 ? 3 : 1;
      for (let i = 0; i < cnt; i++) {
        const angle = base + (i - (cnt - 1) / 2) * 0.25;
        bulletsRef.current.push({ x: player.x, y: player.y, dx: Math.cos(angle) * 9, dy: Math.sin(angle) * 9, size: 5, explosive: lv >= 6 });
      }
      playBeep(600 + lv * 40, 0.05, "triangle");
    };

    const keyDown = (e) => { keys.current[e.key.toLowerCase()] = true; if (e.key === "Escape") onExit(); };
    const keyUp   = (e) => { keys.current[e.key.toLowerCase()] = false; };

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const dx = e.clientX - rect.left - playerRef.current.x;
      const dy = e.clientY - rect.top  - playerRef.current.y;
      const d  = Math.sqrt(dx * dx + dy * dy);
      if (d > 5) aimDirRef.current = { x: dx / d, y: dy / d };
    };
    const handleMouseDown = () => { isShootingRef.current = true;  playBeep(300, 0.1, "sine"); };
    const handleMouseUp   = () => { isShootingRef.current = false; };

    window.addEventListener("keydown",  keyDown);
    window.addEventListener("keyup",    keyUp);
    canvas.addEventListener("pointermove", handleMouseMove);
    canvas.addEventListener("pointerdown", handleMouseDown);
    window.addEventListener("pointerup",   handleMouseUp);

    const update = () => {
      if (gameOverRef.current) return;
      animationRef.current = requestAnimationFrame(update);
      frameCountRef.current++;
      const player    = playerRef.current;
      const timeScale = slowMoRef.current > 0 ? (slowMoRef.current--, 0.4) : 1;
      const W = canvas.width; const H = canvas.height;

      ctx.save();
      if (shakeRef.current > 0) { ctx.translate((Math.random() - 0.5) * 15, (Math.random() - 0.5) * 15); shakeRef.current--; }

      ctx.fillStyle = "rgba(0,0,0,0.35)"; ctx.fillRect(0, 0, W, H);
      ctx.strokeStyle = "rgba(0,245,255,0.04)"; ctx.lineWidth = 0.5;
      const cols = Math.ceil(W / TILE); const rows = Math.ceil(H / TILE);
      for (let i = 0; i <= cols; i++) { ctx.beginPath(); ctx.moveTo(i * TILE, 0); ctx.lineTo(i * TILE, H); ctx.stroke(); }
      for (let j = 0; j <= rows; j++) { ctx.beginPath(); ctx.moveTo(0, j * TILE); ctx.lineTo(W, j * TILE); ctx.stroke(); }
      if (frameCountRef.current % 8 === 0) {
        const rx = Math.floor(Math.random() * cols) * TILE; const ry = Math.floor(Math.random() * rows) * TILE;
        ctx.fillStyle = `rgba(0,255,136,${Math.random() * 0.06})`; ctx.fillRect(rx + 1, ry + 1, TILE - 2, TILE - 2);
      }

      // Movement: keyboard + left joystick (normalised so diagonal ≠ faster)
      let mvx = 0, mvy = 0;
      if (keys.current["w"]) mvy -= 1;
      if (keys.current["s"]) mvy += 1;
      if (keys.current["a"]) mvx -= 1;
      if (keys.current["d"]) mvx += 1;
      const jm = moveJoyRef.current;
      mvx += jm.x; mvy += jm.y;
      const mvlen = Math.sqrt(mvx * mvx + mvy * mvy);
      if (mvlen > 1) { mvx /= mvlen; mvy /= mvlen; }
      player.x += mvx * player.speed * timeScale;
      player.y += mvy * player.speed * timeScale;
      player.x = Math.max(player.size, Math.min(W - player.size, player.x));
      player.y = Math.max(player.size, Math.min(H - player.size, player.y));

      // Right joystick → aim + auto-fire
      const ja    = aimJoyRef.current;
      const jaMag = Math.sqrt(ja.x * ja.x + ja.y * ja.y);
      if (jaMag > 0.15) {
        aimDirRef.current     = { x: ja.x / jaMag, y: ja.y / jaMag };
        isShootingRef.current = true;
      } else if (jaMag < 0.05 && !keys.current[" "]) {
        // Only reset if mouse isn't holding (mouse sets it via handleMouseDown)
      }

      // Shooting
      if (isShootingRef.current) {
        shootCdRef.current--;
        if (shootCdRef.current <= 0) { shoot(); shootCdRef.current = Math.max(6, 15 - levelRef.current); }
      }

      // Laser (lvl 10)
      if (levelRef.current >= 10 && isShootingRef.current) {
        const angle = Math.atan2(aimDirRef.current.y, aimDirRef.current.x);
        ctx.strokeStyle = "rgba(0,245,255,0.85)"; ctx.lineWidth = 3; ctx.shadowColor = "#00f5ff"; ctx.shadowBlur = 12;
        ctx.beginPath(); ctx.moveTo(player.x, player.y); ctx.lineTo(player.x + Math.cos(angle) * 2000, player.y + Math.sin(angle) * 2000); ctx.stroke(); ctx.shadowBlur = 0;
        enemiesRef.current.forEach((e) => { const ae = Math.atan2(e.y - player.y, e.x - player.x); if (Math.abs(ae - angle) < 0.1) e.hp -= 0.5; });
        playBeep(900, 0.01, "square");
      }

      // Aim indicator
      const aim = aimDirRef.current; const al = Math.sqrt(aim.x * aim.x + aim.y * aim.y);
      if (al > 0.1) {
        ctx.strokeStyle = "rgba(0,245,255,0.22)"; ctx.lineWidth = 1; ctx.setLineDash([4, 6]);
        ctx.beginPath(); ctx.moveTo(player.x, player.y); ctx.lineTo(player.x + (aim.x / al) * 50, player.y + (aim.y / al) * 50); ctx.stroke(); ctx.setLineDash([]);
      }

      // Player
      ctx.shadowColor = "#00f5ff"; ctx.shadowBlur = 16; ctx.fillStyle = "#00f5ff";
      ctx.fillRect(player.x - player.size, player.y - player.size, player.size * 2, player.size * 2);
      ctx.fillStyle = "#000"; ctx.fillRect(player.x - 2, player.y - 2, 4, 4); ctx.shadowBlur = 0;

      // Bullets (reverse iterate)
      for (let bi = bulletsRef.current.length - 1; bi >= 0; bi--) {
        const b = bulletsRef.current[bi];
        b.x += b.dx * timeScale; b.y += b.dy * timeScale;
        ctx.fillStyle = "#00ff88"; ctx.shadowColor = "#00ff88"; ctx.shadowBlur = 8;
        ctx.fillRect(b.x - b.size / 2, b.y - b.size / 2, b.size, b.size); ctx.shadowBlur = 0;
        if (b.x < 0 || b.x > W || b.y < 0 || b.y > H) bulletsRef.current.splice(bi, 1);
      }

      // Enemies (reverse iterate to avoid index shift bugs)
      for (let ei = enemiesRef.current.length - 1; ei >= 0; ei--) {
        const enemy = enemiesRef.current[ei];
        const dx = player.x - enemy.x; const dy = player.y - enemy.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        enemy.x += (dx / dist) * enemy.speed * 0.6 * timeScale;
        enemy.y +=               enemy.speed       * timeScale;
        const hue = enemy.boss ? (bossHueRef.current = (bossHueRef.current + 2) % 360) : (levelRef.current * 25) % 360;
        ctx.shadowColor = `hsl(${hue},90%,60%)`; ctx.shadowBlur = 12; ctx.fillStyle = `hsl(${hue},90%,60%)`;
        ctx.fillRect(enemy.x - enemy.size, enemy.y - enemy.size, enemy.size * 2, enemy.size * 2); ctx.shadowBlur = 0;
        if (enemy.boss) {
          const bw = enemy.size * 2;
          ctx.fillStyle = "rgba(0,0,0,0.6)"; ctx.fillRect(enemy.x - enemy.size, enemy.y - enemy.size - 10, bw, 4);
          ctx.fillStyle = "#ff0055"; ctx.fillRect(enemy.x - enemy.size, enemy.y - enemy.size - 10, bw * (enemy.hp / 15), 4);
        }
        // Player collision
        if (dist < enemy.size + player.size && !gameOverRef.current) {
          playBeep(100, 0.4, "sawtooth"); vib([150, 50, 250]);
          if (scoreRef.current > bestScoreRef.current) { bestScoreRef.current = scoreRef.current; localStorage.setItem("neural-override-best", scoreRef.current); setDisplayBest(scoreRef.current); }
          gameOverRef.current = true; ctx.restore();
          setTimeout(() => setGameOver(true), 350); return;
        }
        // Bullet collision (reverse)
        for (let bi = bulletsRef.current.length - 1; bi >= 0; bi--) {
          const b = bulletsRef.current[bi];
          const bdx = enemy.x - b.x; const bdy = enemy.y - b.y;
          if (Math.sqrt(bdx * bdx + bdy * bdy) < enemy.size) {
            enemy.hp--; bulletsRef.current.splice(bi, 1);
            if (b.explosive && enemy.hp <= 0) {
              enemiesRef.current.forEach((e2) => { const ex = e2.x - enemy.x; const ey = e2.y - enemy.y; if (Math.sqrt(ex * ex + ey * ey) < 80) e2.hp -= 2; });
              shakeRef.current = 10;
            }
            break;
          }
        }
        if (enemy.hp <= 0) {
          if (enemy.boss) { shakeRef.current = 40; glitchRef.current = 40; slowMoRef.current = 40; vib([30, 20, 60, 20, 100]); shockwaveRef.current = { x: enemy.x, y: enemy.y, radius: 10, alpha: 1 }; }
          else { vib(20); }
          enemiesRef.current.splice(ei, 1);
          scoreRef.current += enemy.boss ? 500 : 40; setDisplayScore(scoreRef.current);
          playBeep(enemy.boss ? 150 : 400, 0.1);
        }
      }

      scoreRef.current++; if (frameCountRef.current % 60 === 0) setDisplayScore(scoreRef.current);

      if (scoreRef.current % 2000 === 0 && scoreRef.current > 0) {
        levelRef.current++; setDisplayLevel(levelRef.current);
        setStory(STORY_LINES[Math.min(levelRef.current - 1, STORY_LINES.length - 1)]);
        shakeRef.current = 20; glitchRef.current = 25; vib([40, 30, 40]); playBeep(1000, 0.15, "triangle");
      }

      if (glitchRef.current > 0) {
        try { const sh = 20 + Math.random() * 50; const gy = Math.random() * (H - sh); const d = ctx.getImageData(0, gy, W, sh); ctx.putImageData(d, Math.random() * 40 - 20, gy); } catch (_) {}
        ctx.fillStyle = `rgba(255,0,85,${Math.random() * 0.08})`; ctx.fillRect(0, 0, W, H); glitchRef.current--;
      }
      if (levelRef.current > 3) {
        ctx.globalCompositeOperation = "lighter";
        ctx.fillStyle = "rgba(255,0,0,0.04)"; ctx.fillRect(-2, 0, W, H);
        ctx.fillStyle = "rgba(0,255,255,0.04)"; ctx.fillRect(2, 0, W, H);
        ctx.globalCompositeOperation = "source-over";
      }
      if (levelRef.current >= 6) { pulseRef.current += 0.05; ctx.fillStyle = `rgba(255,0,0,${((Math.sin(pulseRef.current) + 1) / 2) * 0.12})`; ctx.fillRect(0, 0, W, H); }
      if (shockwaveRef.current) {
        const s = shockwaveRef.current;
        ctx.beginPath(); ctx.arc(s.x, s.y, s.radius, 0, Math.PI * 2); ctx.strokeStyle = `rgba(0,245,255,${s.alpha})`; ctx.lineWidth = 3; ctx.stroke();
        s.radius += 8; s.alpha -= 0.04; if (s.alpha <= 0) shockwaveRef.current = null;
      }
      ctx.restore();

      spawnTimerRef.current++;
      if (spawnTimerRef.current > Math.max(30, 120 - levelRef.current * 5)) { spawnEnemy(); spawnTimerRef.current = 0; }
    };

    update();

    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener("keydown",  keyDown);
      window.removeEventListener("keyup",    keyUp);
      window.removeEventListener("resize",   resize);
      canvas.removeEventListener("pointermove", handleMouseMove);
      canvas.removeEventListener("pointerdown", handleMouseDown);
      window.removeEventListener("pointerup",   handleMouseUp);
      if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
        try { audioCtxRef.current.close(); } catch (_) {}
      }
    };
  }, [gameState, playBeep, onExit]);

  const btnBase = { padding: "16px 36px", background: "transparent", fontFamily: "monospace", fontSize: 13, letterSpacing: 4, cursor: "pointer", WebkitTapHighlightColor: "transparent", touchAction: "manipulation" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "#000", overflow: "hidden", touchAction: "none", userSelect: "none", WebkitUserSelect: "none" }}>
      <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, display: "block" }} />

      <AnimatePresence>
        {gameState === "start" && (
          <motion.div key="start" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "monospace", color: "#fff" }}>
            <GridBackground />
            <div className="absolute inset-0 pointer-events-none" />
            <div className="absolute top-1/4 left-1/3 w-[600px] h-[600px] rounded-full pointer-events-none hidden dark:block" style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.07) 0%, transparent 70%)" }} />
            <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full pointer-events-none hidden dark:block" style={{ background: "radial-gradient(circle, hsl(var(--secondary) / 0.05) 0%, transparent 70%)" }} />
            <div className="absolute right-[15%] top-1/2 w-[450px] h-[450px] rounded-full pointer-events-none block dark:hidden" style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.08) 0%, transparent 65%)", transform: "translateY(-50%)" }} />
            <div className="absolute inset-0 bg-grid pointer-events-none dark:opacity-40 opacity-15" />
            <div className="absolute inset-0"><ParticleField /></div>
            <div style={{ position: "relative", zIndex: 1, textAlign: "center", padding: "0 24px" }}>
              <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                <div style={{ fontSize: 11, letterSpacing: 10, color: "rgb(152,107,243)", opacity: 0.5, marginBottom: 10, textShadow: "0 0 10px #00f5ff" }}>SYSTEM / ARCADE</div>
                <div style={{ fontSize: "clamp(22px, 7vw, 32px)", letterSpacing: 8, color: "#fff", fontWeight: 900, textShadow: "0 0 30px rgba(0,245,255,0.5)" }}>NEURAL OVERRIDE</div>
                <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.4, duration: 0.6 }} style={{ height: 1, background: "linear-gradient(90deg, transparent, rgb(125,78,218), transparent)", marginTop: 12, transformOrigin: "center" }} />
                <div style={{ fontSize: 10, letterSpacing: 3, color: "rgba(255,255,255,0.5)", marginTop: 12 }}>DEFEND THE GRID. BECOME THE SYSTEM.</div>
              </motion.div>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} style={{ marginTop: 28, marginBottom: 8, textAlign: "left", display: "inline-block" }}>
                {(isMobile ? [["MOVE", "LEFT STICK"], ["AIM + FIRE", "RIGHT STICK"], ["EXIT", "EXIT BUTTON"]] : [["MOVE", "WASD"], ["AIM", "MOUSE"], ["SHOOT", "CLICK / HOLD"], ["EXIT", "ESC"]]).map(([action, key]) => (
                  <div key={action} style={{ display: "flex", justifyContent: "space-between", gap: 40, fontSize: 10, letterSpacing: 2, marginBottom: 8 }}>
                    <span style={{ color: "rgba(255,255,255,0.5)" }}>{action}</span>
                    <span style={{ color: "rgb(125,78,218)" }}>{key}</span>
                  </div>
                ))}
              </motion.div>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} style={{ marginTop: 28, display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
                <button onClick={() => { vib(40); setGameState("playing"); }} style={{ ...btnBase, border: "1px solid rgb(125,78,218)", color: "rgb(125,78,218)", textShadow: "0 0 8px rgb(125,78,218)", boxShadow: "0 0 16px rgba(162,0,255,0.3)" }}>▶ ENGAGE</button>
                {bestScoreRef.current > 0 && <div style={{ fontSize: 10, letterSpacing: 3, color: "rgba(255,255,255,0.5)" }}>BEST: {bestScoreRef.current}</div>}
                <button onClick={onExit} style={{ ...btnBase, border: "none", fontSize: 10, color: "rgba(255,255,255,0.25)", padding: "10px 20px" }}>← BACK</button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {gameState === "playing" && (
        <div style={{ position: "absolute", top: "max(16px, env(safe-area-inset-top))", left: "50%", transform: "translateX(-50%)", textAlign: "center", pointerEvents: "none", fontFamily: "monospace", textShadow: "0 0 10px #00f5ff", paddingTop: 8 }}>
          <div style={{ fontSize: 10, letterSpacing: 6, color: "rgb(125,78,218)", opacity: 0.5 }}>NEURAL OVERRIDE</div>
          <div style={{ marginTop: 4, fontSize: 11, color: "#fff", opacity: 0.85 }}>{story}</div>
          <div style={{ marginTop: 4, fontSize: 10, opacity: 0.5 }}>{displayScore} &nbsp;|&nbsp; BEST {displayBest} &nbsp;|&nbsp; LVL {displayLevel}</div>
        </div>
      )}

      {gameState === "playing" && isMobile && (
        <>
          <div style={{ position: "absolute", bottom: "max(32px, env(safe-area-inset-bottom))", left: 24, zIndex: 10 }}>
            <VirtualJoystick onMove={handleMoveJoy} color="#00f5ff" label="MOVE" />
          </div>
          <div style={{ position: "absolute", bottom: "max(32px, env(safe-area-inset-bottom))", right: 24, zIndex: 10 }}>
            <VirtualJoystick onMove={handleAimJoy} color="#00ff88" label="AIM · FIRE" />
          </div>
          <div style={{ position: "absolute", top: "max(16px, env(safe-area-inset-top))", right: 16, zIndex: 10, paddingTop: 8 }}>
            <button onTouchStart={(e) => { e.preventDefault(); vib(20); onExit(); }}
              style={{ background: "transparent", border: "1px solid rgba(255,0,85,0.4)", color: "rgba(255,0,85,0.6)", fontFamily: "monospace", fontSize: 9, letterSpacing: 2, padding: "8px 12px", touchAction: "none", WebkitTapHighlightColor: "transparent" }}>ESC</button>
          </div>
        </>
      )}

      {gameState === "playing" && !isMobile && (
        <div style={{ position: "absolute", bottom: 16, left: "50%", transform: "translateX(-50%)", color: "rgba(255,255,255,0.15)", fontSize: 9, fontFamily: "monospace", letterSpacing: 3, pointerEvents: "none", whiteSpace: "nowrap" }}>
          WASD MOVE &nbsp;·&nbsp; MOUSE AIM &nbsp;·&nbsp; CLICK SHOOT &nbsp;·&nbsp; ESC EXIT
        </div>
      )}

      <AnimatePresence>
        {gameOver && (
          <motion.div key="gameover" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.88)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#fff", fontFamily: "monospace" }}>
            <motion.h1 initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              style={{ fontSize: "clamp(22px, 7vw, 36px)", marginBottom: 8, color: "#ff0055", textShadow: "0 0 24px #ff0055", letterSpacing: 6 }}>SYSTEM FAILURE</motion.h1>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} style={{ textAlign: "center" }}>
              <p style={{ opacity: 0.5, fontSize: 11, letterSpacing: 3, marginBottom: 6 }}>FINAL SCORE</p>
              <p style={{ fontSize: 44, letterSpacing: 4, color: "#fff", marginBottom: 4 }}>{scoreRef.current}</p>
              <p style={{ opacity: 0.3, fontSize: 10, letterSpacing: 3, marginBottom: 16 }}>LEVEL {levelRef.current}</p>
              {scoreRef.current >= bestScoreRef.current && scoreRef.current > 0 && (
                <motion.p initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} style={{ color: "#00f5ff", fontSize: 11, letterSpacing: 4, textShadow: "0 0 10px #00f5ff", marginBottom: 20 }}>◆ NEW BEST ◆</motion.p>
              )}
              <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                <button onClick={doReset} style={{ ...btnBase, padding: "14px 28px", border: "1px solid #00f5ff", color: "#00f5ff", textShadow: "0 0 8px #00f5ff", boxShadow: "0 0 12px rgba(0,245,255,0.25)" }}>RESTART</button>
                {onExit && <button onClick={onExit} style={{ ...btnBase, padding: "14px 28px", border: "1px solid #ff0055", color: "#ff0055", textShadow: "0 0 8px #ff0055", boxShadow: "0 0 12px rgba(255,0,85,0.25)" }}>EXIT</button>}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SecretGame;